import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import universityAPI from '../../api/university';

const FLAG_DEFINITIONS = [
  {
    key: 'registrationSplit',
    label: 'Role-aware registration flows',
    description: 'Enable the split invite, individual, and domain registration experience for your learners.'
  },
  {
    key: 'oauthFederation',
    label: 'OAuth sign-in and linking',
    description: 'Allow learners to sign in or link accounts with Google and Microsoft providers.'
  }
];

const FeatureFlagManager = () => {
  const [flags, setFlags] = useState({});
  const [universityId, setUniversityId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingKey, setSavingKey] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadFlags = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await universityAPI.getMyUniversity();
        if (!mounted) return;

        const data = response.data || {};
        setUniversityId(data.id || data._id || '');
        const fetchedFlags = data.settings?.featureFlags || {};
        const normalized = FLAG_DEFINITIONS.reduce((acc, flag) => {
          acc[flag.key] = Boolean(fetchedFlags?.[flag.key]);
          return acc;
        }, {});
        setFlags(normalized);
      } catch (err) {
        if (!mounted) return;
        setError('Unable to load feature flag settings.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadFlags();
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = async (flagKey) => {
    if (!universityId) {
      setError('A university context is required to update feature flags.');
      return;
    }

    setSavingKey(flagKey);
    setError('');
    setSuccess('');

    try {
      const nextValue = !flags[flagKey];
      const nextFlags = { ...flags, [flagKey]: nextValue };
      await universityAPI.updateUniversity(universityId, {
        settings: {
          featureFlags: nextFlags
        }
      });
      setFlags(nextFlags);
      setSuccess('Feature flags updated successfully. Changes take effect immediately for new sessions.');
    } catch (err) {
      setError('Failed to update feature flags. Please try again or contact support.');
    } finally {
      setSavingKey('');
    }
  };

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Feature flags
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control access to the new registration and authentication experiences while Phase 3 rolls out. Changes here override the global defaults for your tenant only.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Stack spacing={3}>
          {FLAG_DEFINITIONS.map((flag) => (
            <Paper key={flag.key} variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {flag.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {flag.description}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(flags[flag.key])}
                      onChange={() => handleToggle(flag.key)}
                      color="primary"
                      disabled={savingKey === flag.key}
                    />
                  )}
                  label={flags[flag.key] ? 'Enabled' : 'Disabled'}
                />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default FeatureFlagManager;
